// pages/reports/SalesInvoiceReport.jsx
import React, { useState, useEffect } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import dayjs from 'dayjs';
import Select from 'react-select';
import { DatePicker } from 'antd';
import Table from '../../core/pagination/datatable';
import {
    ChevronUp,
    RotateCcw,
    Sliders,
    StopCircle,
    User,
    Calendar,
    Filter,
    Search,
    TrendingUp,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Package,
    Clock,
    CreditCard,
    PieChart
} from 'feather-icons-react/build/IconComponents';
import { saleActions } from '../../core/action/saleActions';
import { customerActions } from '../../core/action/customerActions';

const { RangePicker } = DatePicker;
const MySwal = withReactContent(Swal);

const SalesInvoiceReport = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // State
    const [sales, setSales] = useState([]);

    const [loading, setLoading] = useState(false);
    const [isFilterVisible, setIsFilterVisible] = useState(false);
    const [reportType, setReportType] = useState('summary');

    // Dropdown options
    const [customers, setCustomers] = useState([]);

    // Pagination
    // NOTE: the shared Datatable component paginates client-side, so we fetch all
    // matching records in one request (large limit) and let the table handle paging.
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 1000,
        total: 0,
        totalPages: 1
    });

    // Filters
    const [filters, setFilters] = useState({
        search: '',
        startDate: dayjs().subtract(30, 'day'),
        endDate: dayjs(),
        customer_id: 0,
        status: '',
        payment_status: '',
        groupBy: 'daily'
    });

    // Client-side search filter (API search param has a SQL bug)
    const filteredSales = React.useMemo(() => {
        const term = (filters.search || '').toLowerCase().trim();
        if (!term || sales.length === 0) return sales;
        return sales.filter(sale => {
            const inv = (sale.invoice_no || '').toLowerCase();
            const cust = (sale.customer_name || '').toLowerCase();
            const ref = (sale.reference_no || sale.reference || '').toLowerCase();
            return inv.includes(term) || cust.includes(term) || ref.includes(term);
        });
    }, [filters.search, sales]);

    // Summary statistics
    const [summary, setSummary] = useState({
        totalSales: 0,
        totalAmount: 0,
        totalPaid: 0,
        totalDue: 0,
        totalTax: 0,
        averageInvoice: 0,
        pendingCount: 0,
        paidCount: 0,
        partialCount: 0,
        cancelledCount: 0
    });

    // Report type options
    const reportTypeOptions = [
        { value: 'summary', label: 'Summary Report' },
        { value: 'detailed', label: 'Detailed Report' },
        { value: 'payment', label: 'Payment Report' },
        { value: 'tax', label: 'Tax Report' },
        { value: 'customer', label: 'Customer-wise Report' }
    ];

    // Group by options
    const groupByOptions = [
        { value: 'daily', label: 'Daily' },
        { value: 'weekly', label: 'Weekly' },
        { value: 'monthly', label: 'Monthly' },
        { value: 'yearly', label: 'Yearly' }
    ];

    // Status filter options
    const statusFilterOptions = [
        { value: '', label: 'All Status' },
        { value: 'pending', label: 'Pending' },
        { value: 'confirmed', label: 'Confirmed' },
        { value: 'processing', label: 'Processing' },
        { value: 'shipped', label: 'Shipped' },
        { value: 'delivered', label: 'Delivered' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    // Payment status filter options
    const paymentStatusFilterOptions = [
        { value: '', label: 'All Payment' },
        { value: 'paid', label: 'Paid' },
        { value: 'partial', label: 'Partial' },
        { value: 'unpaid', label: 'Unpaid' }
    ];

    // Date range presets
    const datePresets = {
        'Today': [dayjs(), dayjs()],
        'Yesterday': [dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')],
        'Last 7 Days': [dayjs().subtract(7, 'day'), dayjs()],
        'Last 30 Days': [dayjs().subtract(30, 'day'), dayjs()],
        'This Month': [dayjs().startOf('month'), dayjs().endOf('month')],
        'Last Month': [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')],
        'This Quarter': [dayjs().startOf('quarter'), dayjs().endOf('quarter')],
        'Last Quarter': [dayjs().subtract(1, 'quarter').startOf('quarter'), dayjs().subtract(1, 'quarter').endOf('quarter')],
        'This Year': [dayjs().startOf('year'), dayjs().endOf('year')],
        'Last Year': [dayjs().subtract(1, 'year').startOf('year'), dayjs().subtract(1, 'year').endOf('year')]
    };

    // Load initial data
    useEffect(() => {
        loadCustomers();
        loadReportData();
    }, []);

    // Load customers
    const loadCustomers = async () => {
        try {
            const result = await customerActions.getCustomers({ limit: 1000 });
            if (result.success) {
                setCustomers([
                    { value: 0, label: 'All Customers' },
                    ...result.data.map(c => ({
                        value: c.id,
                        label: `${c.name} (${c.phone || 'No phone'})`
                    }))
                ]);
            }
        } catch (error) {
            console.error('Error loading customers:', error);
        }
    };

    // Load report data
    const loadReportData = async (page = pagination.page) => {
        try {
            setLoading(true);

            const params = {
                page,
                limit: pagination.limit,
                start_date: filters.startDate ? filters.startDate.format('YYYY-MM-DD') : undefined,
                end_date: filters.endDate ? filters.endDate.format('YYYY-MM-DD') : undefined,
                customer_id: filters.customer_id || undefined,
                status: filters.status || undefined,
                payment_status: filters.payment_status || undefined,
                group_by: filters.groupBy,
                report_type: reportType
            };

            const result = await saleActions.getSales(params);

            if (result.success) {
                setSales(result.data || []);
                setPagination({
                    page: result.page || page,
                    limit: pagination.limit,
                    total: result.total || 0,
                    totalPages: result.totalPages || 1
                });

                // Update summary statistics — prefer API grand-totals (all records),
                // fall back to current-page calculation if not provided.
                if (result.summary) {
                    applySummary(result.summary);
                } else {
                    calculateSummary(result.data || []);
                }
            } else {
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result.message || 'Failed to load report data',
                    timer: 2000
                });
            }
        } catch (error) {
            console.error('Error loading report data:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load report data',
                timer: 2000
            });
        } finally {
            setLoading(false);
        }
    };

    // Calculate summary from sales data
    const calculateSummary = (data) => {
        const totalSales = data.length;
        const totalAmount = data.reduce((sum, s) => sum + (s.grand_total || 0), 0);
        const totalPaid = data.reduce((sum, s) => sum + (s.paid_amount || 0), 0);
        const totalDue = data.reduce((sum, s) => sum + (s.due_amount || 0), 0);
        const totalTax = data.reduce((sum, s) => sum + (s.tax || 0), 0);

        const pendingCount = data.filter(s => s.status === 'pending').length;
        const paidCount = data.filter(s => s.payment_status === 'paid').length;
        const partialCount = data.filter(s => s.payment_status === 'partial').length;
        const cancelledCount = data.filter(s => s.status === 'cancelled').length;

        setSummary({
            totalSales,
            totalAmount,
            totalPaid,
            totalDue,
            totalTax,
            averageInvoice: totalSales > 0 ? totalAmount / totalSales : 0,
            pendingCount,
            paidCount,
            partialCount,
            cancelledCount
        });
    };

    // Apply grand-total summary returned by the API (covers ALL records, not just current page)
    const applySummary = (s) => {
        const totalSales = parseInt(s.total_sales || 0);
        const totalAmount = parseFloat(s.total_amount || 0);
        setSummary({
            totalSales,
            totalAmount,
            totalPaid: parseFloat(s.total_paid || 0),
            totalDue: parseFloat(s.total_due || 0),
            totalTax: parseFloat(s.total_tax || 0),
            averageInvoice: s.average_invoice != null
                ? parseFloat(s.average_invoice)
                : (totalSales > 0 ? totalAmount / totalSales : 0),
            pendingCount: parseInt(s.pending_count || 0),
            paidCount: parseInt(s.paid_count || 0),
            partialCount: parseInt(s.partial_count || 0),
            cancelledCount: parseInt(s.cancelled_count || 0)
        });
    };

    // Handle filter toggle
    const toggleFilterVisibility = () => {
        setIsFilterVisible((prev) => !prev);
    };

    // Handle search — filtering is done client-side via filteredSales
    const handleSearch = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({
            search: '',
            startDate: dayjs().subtract(30, 'day'),
            endDate: dayjs(),
            customer_id: 0,
            status: '',
            payment_status: '',
            groupBy: 'daily'
        });
        setReportType('summary');
        setPagination(prev => ({ ...prev, page: 1 }));
        loadReportData(1);
    };

    // Handle date range change
    const handleDateRangeChange = (dates) => {
        if (dates && dates.length === 2) {
            setFilters(prev => ({
                ...prev,
                startDate: dates[0],
                endDate: dates[1]
            }));
        }
    };

    // Handle filter change
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({ ...prev, [filterName]: value }));
    };

    // Handle report type change
    const handleReportTypeChange = (option) => {
        setReportType(option?.value || 'summary');
        setPagination(prev => ({ ...prev, page: 1 }));
        setTimeout(() => loadReportData(1), 0);
    };

    // Handle group by change
    const handleGroupByChange = (option) => {
        setFilters(prev => ({ ...prev, groupBy: option?.value || 'daily' }));
        setPagination(prev => ({ ...prev, page: 1 }));
        setTimeout(() => loadReportData(1), 0);
    };

    // Handle page change
    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, page }));
        loadReportData(page);
    };

    // Tooltip renderers
    const renderTooltip = (props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    );

    // Table columns based on report type
    const getColumns = () => {
        const baseColumns = [
            {
                title: "Invoice No",
                dataIndex: "invoice_no",
                sorter: true,
                render: (text, record) => (
                    <div>
                        <strong>{text}</strong>
                        <br />
                        <small className="text-muted">{dayjs(record.sale_date).format('DD/MM/YYYY')}</small>
                    </div>
                ),
            },
            {
                title: "Customer",
                dataIndex: "customer_name",
                sorter: true,
                render: (text, record) => (
                    <div>
                        <div>{text}</div>
                        <small className="text-muted">{record.customer_phone || ''}</small>
                    </div>
                ),
            }
        ];

        switch (reportType) {
            case 'detailed':
                return [
                    ...baseColumns,
                    {
                        title: "Subtotal",
                        dataIndex: "subtotal",
                        sorter: true,
                        render: (text) => formatCurrency(text),
                    },
                    {
                        title: "Tax",
                        dataIndex: "tax",
                        sorter: true,
                        render: (text) => formatCurrency(text),
                    },
                    {
                        title: "Discount",
                        dataIndex: "discount",
                        sorter: true,
                        render: (text) => formatCurrency(text),
                    },
                    {
                        title: "Shipping",
                        dataIndex: "shipping",
                        sorter: true,
                        render: (text) => formatCurrency(text),
                    },
                    {
                        title: "Grand Total",
                        dataIndex: "grand_total",
                        sorter: true,
                        render: (text) => <strong className="text-primary">{formatCurrency(text)}</strong>,
                    },
                    {
                        title: "Paid",
                        dataIndex: "paid_amount",
                        sorter: true,
                        render: (text) => <span className="text-success">{formatCurrency(text)}</span>,
                    },
                    {
                        title: "Due",
                        dataIndex: "due_amount",
                        sorter: true,
                        render: (text) => (
                            <span className={text > 0 ? 'text-danger' : 'text-success'}>
                                {formatCurrency(text)}
                            </span>
                        ),
                    },
                    {
                        title: "Status",
                        dataIndex: "status",
                        sorter: true,
                        render: (text) => getStatusBadge(text),
                    },
                    {
                        title: "Payment",
                        dataIndex: "payment_status",
                        sorter: true,
                        render: (text) => getPaymentStatusBadge(text),
                    },
                ];

            case 'payment':
                return [
                    ...baseColumns,
                    {
                        title: "Grand Total",
                        dataIndex: "grand_total",
                        sorter: true,
                        render: (text) => formatCurrency(text),
                    },
                    {
                        title: "Paid Amount",
                        dataIndex: "paid_amount",
                        sorter: true,
                        render: (text) => <span className="text-success">{formatCurrency(text)}</span>,
                    },
                    {
                        title: "Due Amount",
                        dataIndex: "due_amount",
                        sorter: true,
                        render: (text) => (
                            <span className={text > 0 ? 'text-danger' : 'text-success'}>
                                {formatCurrency(text)}
                            </span>
                        ),
                    },
                    {
                        title: "Payment Status",
                        dataIndex: "payment_status",
                        sorter: true,
                        render: (text) => getPaymentStatusBadge(text),
                    },
                    {
                        title: "Payments",
                        dataIndex: "payment_count",
                        sorter: true,
                        render: (text) => (
                            <span className="badge bg-info">{text || 0}</span>
                        ),
                    },
                    {
                        title: "Last Payment",
                        dataIndex: "last_payment_date",
                        render: (text, record) => (
                            <div>
                                {text ? dayjs(text).format('DD/MM/YYYY') : 'N/A'}
                                {record.last_payment_method && (
                                    <small className="d-block text-muted">
                                        via {record.last_payment_method}
                                    </small>
                                )}
                            </div>
                        ),
                    },
                ];

            case 'tax':
                return [
                    ...baseColumns,
                    {
                        title: "Taxable Amount",
                        dataIndex: "subtotal",
                        sorter: true,
                        render: (text) => formatCurrency(text),
                    },
                    {
                        title: "Tax Amount",
                        dataIndex: "tax",
                        sorter: true,
                        render: (text) => <span className="text-warning">{formatCurrency(text)}</span>,
                    },
                    {
                        title: "Tax %",
                        render: (text, record) => {
                            const taxPercent = record.subtotal > 0
                                ? ((record.tax / record.subtotal) * 100).toFixed(2)
                                : 0;
                            return <span>{taxPercent}%</span>;
                        },
                    },
                    {
                        title: "Grand Total",
                        dataIndex: "grand_total",
                        sorter: true,
                        render: (text) => formatCurrency(text),
                    },
                ];

            case 'customer':
                return [
                    {
                        title: "Customer",
                        dataIndex: "customer_name",
                        sorter: true,
                        render: (text, record) => (
                            <div>
                                <strong>{text}</strong>
                                <br />
                                <small className="text-muted">{record.customer_phone || ''}</small>
                            </div>
                        ),
                    },
                    {
                        title: "Total Invoices",
                        dataIndex: "invoice_count",
                        sorter: true,
                        render: (text) => <span className="badge bg-primary">{text || 1}</span>,
                    },
                    {
                        title: "Total Amount",
                        render: (text, record) => {
                            const total = sales
                                .filter(s => s.customer_id === record.customer_id)
                                .reduce((sum, s) => sum + (s.grand_total || 0), 0);
                            return formatCurrency(total);
                        },
                    },
                    {
                        title: "Total Paid",
                        render: (text, record) => {
                            const total = sales
                                .filter(s => s.customer_id === record.customer_id)
                                .reduce((sum, s) => sum + (s.paid_amount || 0), 0);
                            return <span className="text-success">{formatCurrency(total)}</span>;
                        },
                    },
                    {
                        title: "Total Due",
                        render: (text, record) => {
                            const total = sales
                                .filter(s => s.customer_id === record.customer_id)
                                .reduce((sum, s) => sum + (s.due_amount || 0), 0);
                            return (
                                <span className={total > 0 ? 'text-danger' : 'text-success'}>
                                    {formatCurrency(total)}
                                </span>
                            );
                        },
                    },
                ];

            default: // summary report
                return [
                    ...baseColumns,
                    {
                        title: "Amount",
                        dataIndex: "grand_total",
                        sorter: true,
                        render: (text) => formatCurrency(text),
                    },
                    {
                        title: "Paid",
                        dataIndex: "paid_amount",
                        sorter: true,
                        render: (text) => <span className="text-success">{formatCurrency(text)}</span>,
                    },
                    {
                        title: "Due",
                        dataIndex: "due_amount",
                        sorter: true,
                        render: (text) => (
                            <span className={text > 0 ? 'text-danger' : 'text-success'}>
                                {formatCurrency(text)}
                            </span>
                        ),
                    },
                    {
                        title: "Status",
                        dataIndex: "status",
                        sorter: true,
                        render: (text) => getStatusBadge(text),
                    },
                    {
                        title: "Payment",
                        dataIndex: "payment_status",
                        sorter: true,
                        render: (text) => getPaymentStatusBadge(text),
                    },
                    {
                        title: "Items/Qty",
                        render: (text, record) => (
                            <div>
                                <span className="badge bg-info me-1">Items: {record.items_count || 0}</span>
                                <span className="badge bg-secondary">Qty: {record.total_qty || 0}</span>
                            </div>
                        ),
                    },
                ];
        }
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const badges = {
            'pending': { class: 'badge bg-warning', icon: <Clock size={12} /> },
            'confirmed': { class: 'badge bg-info', icon: <CheckCircle size={12} /> },
            'processing': { class: 'badge bg-primary', icon: <TrendingUp size={12} /> },
            'shipped': { class: 'badge bg-secondary', icon: <Package size={12} /> },
            'delivered': { class: 'badge bg-success', icon: <CheckCircle size={12} /> },
            'cancelled': { class: 'badge bg-danger', icon: <XCircle size={12} /> }
        };
        const badge = badges[status] || { class: 'badge bg-secondary', icon: null };
        return (
            <span className={badge.class}>
                {badge.icon} {status}
            </span>
        );
    };

    // Get payment status badge
    const getPaymentStatusBadge = (status) => {
        const badges = {
            'paid': { class: 'badge bg-success', icon: <CheckCircle size={12} /> },
            'partial': { class: 'badge bg-warning', icon: <AlertTriangle size={12} /> },
            'unpaid': { class: 'badge bg-danger', icon: <XCircle size={12} /> }
        };
        const badge = badges[status] || { class: 'badge bg-secondary', icon: null };
        return (
            <span className={badge.class}>
                {badge.icon} {status}
            </span>
        );
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4>Sales Invoice Report</h4>
                            <h6>Generate and analyze sales invoice reports</h6>
                        </div>
                    </div>

                    <ul className="table-top-head">


                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <button className="btn btn-outline-secondary" onClick={() => loadReportData()}>
                                    <RotateCcw size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <button
                                    className={data ? "btn btn-outline-secondary active" : "btn btn-outline-secondary"}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>

                {/* Summary Cards */}
                <div className="row mb-4">
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title text-muted">Total Invoices</h5>
                                <h3 className="mb-0 text-primary">{summary.totalSales}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title text-muted">Total Amount</h5>
                                <h3 className="mb-0 text-primary">{formatCurrency(summary.totalAmount)}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title text-muted">Total Paid</h5>
                                <h3 className="mb-0 text-success">{formatCurrency(summary.totalPaid)}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title text-muted">Total Due</h5>
                                <h3 className="mb-0 text-danger">{formatCurrency(summary.totalDue)}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title text-muted">Total Tax</h5>
                                <h3 className="mb-0 text-warning">{formatCurrency(summary.totalTax)}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title text-muted">Avg. Invoice</h5>
                                <h3 className="mb-0 text-info">{formatCurrency(summary.averageInvoice)}</h3>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Status Summary Cards */}
                <div className="row mb-4">
                    <div className="col-xl-3 col-sm-6 col-12">
                        <div className="card bg-success bg-opacity-10">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="text-muted mb-1">Paid Invoices</h6>
                                        <h3 className="mb-0 text-success">{summary.paidCount}</h3>
                                    </div>
                                    <div className="bg-success rounded-circle p-3">
                                        <CheckCircle size={24} className="text-white" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-sm-6 col-12">
                        <div className="card bg-warning bg-opacity-10">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="text-muted mb-1">Partial Paid</h6>
                                        <h3 className="mb-0 text-warning">{summary.partialCount}</h3>
                                    </div>
                                    <div className="bg-warning rounded-circle p-3">
                                        <AlertTriangle size={24} className="text-white" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-sm-6 col-12">
                        <div className="card bg-danger bg-opacity-10">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="text-muted mb-1">Unpaid</h6>
                                        <h3 className="mb-0 text-danger">{summary.totalSales - summary.paidCount - summary.partialCount}</h3>
                                    </div>
                                    <div className="bg-danger rounded-circle p-3">
                                        <XCircle size={24} className="text-white" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-sm-6 col-12">
                        <div className="card bg-info bg-opacity-10">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="text-muted mb-1">Pending</h6>
                                        <h3 className="mb-0 text-info">{summary.pendingCount}</h3>
                                    </div>
                                    <div className="bg-info rounded-circle p-3">
                                        <Clock size={24} className="text-white" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters Card */}
                <div className="card table-list-card">
                    <div className="card-body">
                        <div className="table-top">
                            <div className="search-set">
                                <div className="search-input">
                                    <input
                                        type="text"
                                        placeholder="Search by invoice, customer..."
                                        className="form-control form-control-sm formsearch"
                                        value={filters.search}
                                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                    />
                                    <button className="btn btn-searchset" onClick={handleSearch}>
                                        <Search size={16} />
                                    </button>
                                </div>
                            </div>

                            <div className="search-path">
                                <div className="d-flex align-items-center">
                                    <button
                                        className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`}
                                        onClick={toggleFilterVisibility}
                                    >
                                        <Filter size={18} />
                                    </button>
                                </div>
                            </div>

                            <div className="form-sort">
                                <Sliders size={16} className="info-img me-2" />
                                <Select
                                    className="select"
                                    options={reportTypeOptions}
                                    value={reportTypeOptions.find(opt => opt.value === reportType)}
                                    onChange={handleReportTypeChange}
                                    placeholder="Report Type"
                                    isClearable={false}
                                />
                            </div>
                        </div>

                        {/* Filter Panel */}
                        <div
                            className={`card${isFilterVisible ? ' visible' : ''}`}
                            id="filter_inputs"
                            style={{ display: isFilterVisible ? 'block' : 'none' }}
                        >
                            <div className="card-body pb-0">
                                <div className="row">
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <User size={16} className="info-img" />
                                            <Select
                                                options={customers}
                                                value={customers.find(c => c.value === filters.customer_id)}
                                                onChange={(opt) => handleFilterChange('customer_id', opt?.value || 0)}
                                                placeholder="All Customers"
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <StopCircle size={16} className="info-img" />
                                            <Select
                                                options={statusFilterOptions}
                                                value={statusFilterOptions.find(s => s.value === filters.status)}
                                                onChange={(opt) => handleFilterChange('status', opt?.value || '')}
                                                placeholder="All Status"
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <CreditCard size={16} className="info-img" />
                                            <Select
                                                options={paymentStatusFilterOptions}
                                                value={paymentStatusFilterOptions.find(p => p.value === filters.payment_status)}
                                                onChange={(opt) => handleFilterChange('payment_status', opt?.value || '')}
                                                placeholder="All Payment"
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <PieChart size={16} className="info-img" />
                                            <Select
                                                options={groupByOptions}
                                                value={groupByOptions.find(g => g.value === filters.groupBy)}
                                                onChange={handleGroupByChange}
                                                placeholder="Group By"
                                                isClearable={false}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-6 col-sm-12 col-12">
                                        <div className="input-blocks">
                                            <div className="position-relative daterange-wraper">
                                                <Calendar size={16} className="info-img" />
                                                <RangePicker
                                                    value={[filters.startDate, filters.endDate]}
                                                    onChange={handleDateRangeChange}
                                                    format="DD/MM/YYYY"
                                                    placeholder={['From Date', 'To Date']}
                                                    style={{ width: '100%' }}
                                                    ranges={datePresets}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <button className="btn btn-primary w-100" onClick={() => loadReportData(1)}>
                                                <Search size={16} className="me-2" />
                                                Generate Report
                                            </button>
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <button className="btn btn-secondary w-100" onClick={resetFilters}>
                                                <RotateCcw size={16} className="me-2" />
                                                Reset
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Report Table */}
                        <div className="table-responsive">
                            <Table
                                columns={getColumns()}
                                dataSource={filteredSales}
                                loading={loading}
                                pagination={{
                                    current: pagination.page,
                                    pageSize: pagination.limit,
                                    total: pagination.total,
                                    onChange: handlePageChange,
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} invoices`
                                }}
                            />
                        </div>

                        {/* Report Footer with Totals */}
                        {sales.length > 0 && (
                            <div className="row mt-3">
                                <div className="col-md-12">
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-md-2">
                                                    <p className="mb-1 text-muted">Total Records</p>
                                                    <h6 className="fw-bold">{pagination.total}</h6>
                                                </div>
                                                <div className="col-md-2">
                                                    <p className="mb-1 text-muted">Total Amount</p>
                                                    <h6 className="fw-bold text-primary">
                                                        {formatCurrency(summary.totalAmount)}
                                                    </h6>
                                                </div>
                                                <div className="col-md-2">
                                                    <p className="mb-1 text-muted">Total Paid</p>
                                                    <h6 className="fw-bold text-success">
                                                        {formatCurrency(summary.totalPaid)}
                                                    </h6>
                                                </div>
                                                <div className="col-md-2">
                                                    <p className="mb-1 text-muted">Total Due</p>
                                                    <h6 className="fw-bold text-danger">
                                                        {formatCurrency(summary.totalDue)}
                                                    </h6>
                                                </div>
                                                <div className="col-md-2">
                                                    <p className="mb-1 text-muted">Total Tax</p>
                                                    <h6 className="fw-bold text-warning">
                                                        {formatCurrency(summary.totalTax)}
                                                    </h6>
                                                </div>
                                                <div className="col-md-2">
                                                    <p className="mb-1 text-muted">Avg. Invoice</p>
                                                    <h6 className="fw-bold text-info">
                                                        {formatCurrency(summary.averageInvoice)}
                                                    </h6>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SalesInvoiceReport;